import './App.css'
import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
const HomePage = lazy(() => import('./Pages/HomePage'))
const LandingPage = lazy(() => import('./Pages/LandingPage'))
import LandingRevamp from './Pages/LandingRevamp'
import AdminKeyGate from './components/AdminKeyGate'
const SignUp = lazy(() => import('./Pages/SignUp'))
const Login = lazy(() => import('./Pages/Login'))
const ForgotPassword = lazy(() => import('./Pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./Pages/ResetPassword'))
const ClientContractReview = lazy(() => import('./Pages/ClientContractReview'))
const MilestoneSubmission = lazy(() => import('./Pages/MilestoneSubmission'))
const MilestoneSubmissionMobile = lazy(() => import('./Pages/MilestoneSubmissionMobile'))
const MilestoneReview = lazy(() => import('./Pages/MilestoneReview'))
import { useIsMobile } from './hooks/useIsMobile'
const ClientPortal = lazy(() => import('./Pages/ClientPortal'))
const ContractReview = lazy(() => import('./Pages/ContractReview'))
const PublicProfile = lazy(() => import('./Pages/PublicProfile'))
const ProductionChecks = lazy(() => import('./Pages/ProductionChecks'))
const LaunchBoard = lazy(() => import('./Pages/LaunchBoard'))
const CostOptimisation = lazy(() => import('./Pages/CostOptimisation'))
const MerkleAdminPage = lazy(() => import('./Pages/MerkleAdminPage'))
const VerifyContract = lazy(() => import('./Pages/VerifyContract'))
const Marketing = lazy(() => import('./Pages/Marketing'))
const ContactUs = lazy(() => import('./Pages/ContactUs'))
const Privacy = lazy(() => import('./Pages/legal/Privacy'))
const ScoreMethodology = lazy(() => import('./Pages/legal/ScoreMethodology'))
const Pricing = lazy(() => import('./Pages/Pricing'))
const Terms = lazy(() => import('./Pages/legal/Terms'))
const WaitlistPage = lazy(() => import('./Pages/WaitlistPage'))
const AdminResponses = lazy(() => import('./Pages/AdminResponses'))
const AdminBilling = lazy(() => import('./Pages/AdminBilling'))
const AdminContractView = lazy(() => import('./Pages/AdminContractView'))
const GuidePage = lazy(() => import('./Pages/GuidePage'))
const GuidesIndex = lazy(() => import('./Pages/GuidesIndex'))
import { useDocumentMeta } from './seo/useDocumentMeta'
import { useAuth } from './contexts/AuthContext'
import { rememberTemplate } from './seo/contractTemplates'
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
const Agentation = lazy(() => import('agentation').then((m) => ({ default: m.Agentation })))



function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isProfileComplete } = useAuth()
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1117]">
        <div className="w-10 h-10 rounded-full border-2 border-[#3cb44f]/30 border-t-[#3cb44f] animate-spin" />
      </div>
    )
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />
  // Authenticated but profile not completed → force them through the signup wizard
  if (!isProfileComplete) return <Navigate to="/signup?step=2&notice=Let%27s+finish+setting+up+your+account+to+continue." replace />
  return <>{children}</>
}

function MilestoneSubmissionRoute() {
  const isMobile = useIsMobile()
  return isMobile ? <MilestoneSubmissionMobile /> : <MilestoneSubmission />
}

function CatchAll() {
  const { isAuthenticated, isProfileComplete, isLoading } = useAuth()
  if (isLoading) return null
  if (isAuthenticated && isProfileComplete) return <Navigate to="/dashboard" replace />
  return <Navigate to="/" replace />
}

const EARLY_ACCESS_CODE_PREFILL_KEY = 'defellix_early_access_prefill_code'

// Grabs ?code=EARLY-XXXXXXXX off the URL on whatever page the user lands on
// first (the early-access email's "Create your account" link) and stashes
// it in localStorage — the query param itself doesn't survive the multi-page
// signup flow (step 1 → step 2 → email verification → redirect to
// /dashboard), but localStorage does, so EarlyAccessModal can read it back
// once the user actually reaches the dashboard.
// An OAuth handoff code is a 64-char hex string minted by the backend
// (hex of 32 random bytes). Early-access coupons are short and alphanumeric.
// This component runs ABOVE <Routes>, so its effect fires before the /login
// and /signup route effects — if it ate `?code=` it would strip the OAuth
// handoff param before Login.tsx could read it, silently breaking Google and
// LinkedIn sign-in. Never consume anything that looks like an OAuth code.
const OAUTH_HANDOFF_CODE = /^[0-9a-f]{64}$/i

function EarlyAccessCodeCapture() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    // `coupon` is the current param. `code` is still honoured for invite
    // emails already sent with the old link shape, but only when it clearly
    // is not an OAuth handoff code.
    const legacy = params.get('code')?.trim()
    const usingLegacy = !!legacy && !OAUTH_HANDOFF_CODE.test(legacy)
    const code = params.get('coupon')?.trim() || (usingLegacy ? legacy : undefined)
    if (!code) return
    try {
      window.localStorage.setItem(EARLY_ACCESS_CODE_PREFILL_KEY, code.toUpperCase())
    } catch {
      // localStorage unavailable (private mode etc.) — the modal just won't autofill.
    }
    // Drop it from the visible URL/history without touching the rest of the
    // query string — and never remove a `code` we didn't consume.
    params.delete('coupon')
    if (usingLegacy) params.delete('code')
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash}`
    window.history.replaceState(null, '', next)
  }, [])
  return null
}

// Guides link to /signup?template=<id>. Remember the template so it survives
// signup/login, then drop the param from the URL. The dashboard opens the
// contract builder and CreateContractForm seeds the new contract from it.
function TemplateCapture() {
  // Keyed on the location, not run once: guide buttons navigate client-side,
  // so App is already mounted when /signup?template=... is reached.
  const { search } = useLocation()
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = params.get('template')?.trim()
    if (!id) return
    rememberTemplate(id)
    params.delete('template')
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash}`
    window.history.replaceState(null, '', next)
  }, [search])
  return null
}

// Nothing in this app resets scroll position on client-side navigation —
// React Router doesn't do it automatically outside its data-router APIs
// (which this app doesn't use), so navigating to a new page kept whatever
// scroll offset the previous page was left at (e.g. opening a guide from
// the bottom of the guides index landed scrolled to the bottom). Runs on
// every pathname/hash change: scrolls to the hash target if there's an
// in-page anchor (e.g. a cross-page link to /landing-revamp#pricing),
// otherwise resets to the top.
function ScrollManager() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'auto' })
        return
      }
    }
    window.scrollTo(0, 0)
  }, [pathname, hash])
  return null
}

function App() {
  // Keeps the tab title and meta tags in step with the route on client-side
  // navigation. The static per-route shells written at build time
  // (scripts/prerender-meta.mjs) cover crawlers that never run JS.
  useDocumentMeta();

  return (
    <>
      <Analytics />
      <SpeedInsights />
      <EarlyAccessCodeCapture />
      <TemplateCapture />
      <ScrollManager />
      {/* Dev-only annotation toolbar — never rendered in production builds */}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <Agentation />
        </Suspense>
      )}
      {/* Route components are lazy-loaded so the landing page doesn't ship the
          dashboard, admin boards or PDF renderer. LandingRevamp stays eager
          because it is the first paint for almost every visitor. */}
      <Suspense fallback={<div className="min-h-screen bg-black" />}>
      <Routes>
        <Route path="/" element={<LandingRevamp />} />
        <Route path="/landing-revamp" element={<LandingRevamp />} />
        {/* The old landing page — no longer public. Gated by the same
            server-validated admin key used by /admin/* pages (see
            AdminKeyGate + GET /api/v1/admin/verify-key). */}
        <Route path="/legacy-landing" element={<AdminKeyGate><LandingPage /></AdminKeyGate>} />
        <Route path="/guides" element={<GuidesIndex />} />
        <Route path="/guides/:slug" element={<GuidePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/score-methodology" element={<ScoreMethodology />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/waitlist" element={<WaitlistPage />} />

        {/* Internal: backend audit / production readiness board */}
        <Route path="/production-checks" element={<ProductionChecks />} />
        <Route path="/launch-board" element={<LaunchBoard />} />
        <Route path="/cost-optimisation" element={<CostOptimisation />} />

        {/* Admin: Merkle batch visualizer — password-gated, no JWT required */}
        <Route path="/merkle-batching-algorithm" element={<MerkleAdminPage />} />

        {/* Admin: form responses dashboard — password-gated */}
        <Route path="/admin/responses" element={<AdminResponses />} />
        <Route path="/admin/billing" element={<AdminBilling />} />
        <Route path="/admin/contractview" element={<AdminContractView />} />

        {/* Internal: marketing planning board (The Unghosted Project) */}
        <Route path="/marketing/*" element={<Marketing />} />

        <Route path="/review-contract/:contractId" element={<ClientContractReview />} />
        <Route path="/submit-milestone/:contractId" element={<ProtectedRoute><MilestoneSubmissionRoute /></ProtectedRoute>} />
        <Route path="/review-milestone/:contractId" element={<MilestoneReview />} />
        <Route path="/portal/:token" element={<ClientPortal />} />
        <Route path="/review/:token" element={<ContractReview />} />
        <Route path="/verify/:contractId" element={<VerifyContract />} />
        {/* All dashboard routes are protected */}
        <Route path="/dashboard/*" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />

        {/* Public profile vanity URL */}
        <Route path="/:userName" element={<PublicProfile />} />

        {/* Catch-all: go to dashboard if logged in, else landing */}
        <Route path="*" element={<CatchAll />} />
      </Routes>
      </Suspense>
    </>
  )
}

export default App
